import { call, delay, put, select } from 'redux-saga/effects';
import { turnMessageOn } from 'store/app/actions';
import { MessageData, ProfilePicture } from 'store/app/types';
import { logoutRequest } from 'store/auth/actions';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { renderErrorMessage } from 'utils/render-error-message';
import { getProfilePictures } from 'store/app/selectors';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { PICTURE_DELAY_TIME } from 'store/app/constants';
import {
  fetchReconciliationLogsFailure,
  fetchReconciliationLogsSuccess,
} from '../actions';
import {
  getReconciliationLogsForOrganisation,
  ReconciliationLogsResponse,
} from '../api';
import { FetchReconciliationLogsRequestAction } from '../types';

export function* fetchReconciliationLogsSaga(
  action: FetchReconciliationLogsRequestAction
) {
  const { organisationId, page, pageSize, filters } = action.payload;
  try {
    const response: ReconciliationLogsResponse = yield call(
      getReconciliationLogsForOrganisation,
      organisationId,
      page,
      pageSize,
      filters
    );
    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const incomingLogs = response?.data?.logs || [];
    const logs = incomingLogs.map((raw: Record<string, unknown>) => {
      const mappingId =
        (typeof raw.mappingId === 'string' && raw.mappingId) ||
        (typeof raw.mapping_id === 'string' && raw.mapping_id) ||
        undefined;
      return { ...raw, mappingId } as (typeof incomingLogs)[number];
    });
    let pp: ProfilePicture[] = yield select(getProfilePictures);
    const requestedUserUuids = new Set<string>();

    for (let i = 0; i < logs.length; i++) {
      const user = logs[i]?.preparerUser;
      if (user && user.hasProfilePicture && !user.profilePicture) {
        const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
        if (ppIdx > -1) {
          logs[i] = {
            ...logs[i],
            preparerUser: {
              ...user,
              profilePicture: pp[ppIdx].profilePicture,
            },
          };
        } else if (!requestedUserUuids.has(user.userUuid)) {
          requestedUserUuids.add(user.userUuid);
          yield put(fetchProfilePictureRequest(user.userUuid));
          yield delay(PICTURE_DELAY_TIME);
          pp = yield select(getProfilePictures);
          const newIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
          if (newIdx > -1) {
            logs[i] = {
              ...logs[i],
              preparerUser: {
                ...user,
                profilePicture: pp[newIdx].profilePicture,
              },
            };
          }
        }
      }
    }

    yield put(
      fetchReconciliationLogsSuccess(
        logs,
        Number(response?.data?.page ?? page ?? 1),
        Number(response?.data?.pageSize ?? pageSize ?? 10),
        Number(response?.data?.totalElements ?? 0),
        Number(response?.data?.totalPages ?? 0)
      )
    );
  } catch (error: any) {
    const status = error?.status;
    if (status === 401 || status === '401' || status === 'UNAUTHORIZED') {
      yield put(
        fetchReconciliationLogsSuccess([], page ?? 1, pageSize ?? 10, 0, 0)
      );
      return;
    }
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchReconciliationLogsFailure(error?.message));
  }
}
